import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/health-records'

export const healthRecordsTutorial: Tutorial = {
  slug: 'health-records',
  title: 'Health Records',
  subtitle: 'Structured data that makes everything else personal',
  date: '2026-07-09',
  category: 'Core Features',
  platform: 'mobile',
  tags: ['Health Records', 'conditions', 'supplements', 'labs', 'allergies'],
  description:
    'Conditions, supplements, treatments, allergies, and lab results — the structured home for everything the Wellness Coach reads to ground its answers.',
  highlights: [
    'Five categories on one screen — no extra landing page.',
    'Each category has a tailored form: labs ask for values, conditions ask for severity.',
    'Trustworthy, boring data entry is the point — the Coach and Goals build on it.',
  ],
  coverImage: `${IMG}/01_conditions.png`,
  sections: [
    {
      heading: 'Five categories, one screen',
      paragraphs: [
        'Health Records opens straight into your entries — no landing page, no extra tap. A row of category chips switches between **Conditions**, **Supplements**, **Treatments**, **Allergies**, and **Lab Results**.',
        'Each entry shows its status at a glance — conditions flagged **ACTIVE** with severity, supplements with **RESOLVED** for anything no longer current.',
      ],
      image: {
        src: `${IMG}/01_conditions.png`,
        alt: 'Health Records conditions list',
        caption: 'Conditions list',
      },
    },
    {
      heading: 'Supplements and treatments',
      paragraphs: [
        'Switching to Supplements shows the same pattern — name, status, dosage, and frequency at a glance.',
      ],
      image: {
        src: `${IMG}/02_supplements.png`,
        alt: 'Health Records supplements list',
        caption: 'Supplements list',
      },
    },
    {
      heading: 'Adding a record',
      paragraphs: [
        'Tap **+** from any category and a structured form opens for that record type — name, description, date, status, and category-specific fields like dosage and frequency for supplements.',
        'Every category has its own tailored form — a lab result form asks for values and reference ranges, a condition form asks for severity — but the shape is always the same: name it, describe it, date it, done.',
      ],
      image: {
        src: `${IMG}/03_add_supplement_form.png`,
        alt: 'Add Supplement form with dosage and frequency fields',
        caption: 'Add Supplement form',
      },
    },
  ],
  whyMatters:
    'This is deliberately boring, form-based data entry — and that\'s the point. The Wellness Coach and Wellness Goals both lean on this data to be accurate and current: a supplement logged here is what the Coach checks before answering a question about interactions, and a condition logged here is what shapes the genetics-based advice you get.',
  relatedSlugs: ['wellness-coach', 'profile-sharing'],
  meta: {
    description:
      'How Health Records works in Livewello: five structured categories that ground your Wellness Coach answers in real data.',
    ogImage: `${IMG}/01_conditions.png`,
  },
}
